import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Trash2, Edit2, Star, Mail, Phone, Globe, MessageSquare, Inbox, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WebsiteCMS() {
  return (
    <div className="space-y-6" data-testid="website-cms-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Website CMS</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage website leads, testimonials, and SEO content. Backend ready — add admin UIs here.
        </p>
      </div>

      <Tabs defaultValue="leads" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-xl p-1 h-12">
          <TabsTrigger value="leads" data-testid="tab-leads" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white rounded-lg">
            <Inbox className="w-4 h-4 mr-2" /> Leads
          </TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white rounded-lg">
            <Star className="w-4 h-4 mr-2" /> Testimonials
          </TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white rounded-lg">
            <Globe className="w-4 h-4 mr-2" /> SEO Pages
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-6"><LeadsTab /></TabsContent>
        <TabsContent value="testimonials" className="mt-6"><TestimonialsTab /></TabsContent>
        <TabsContent value="seo" className="mt-6"><SEOTab /></TabsContent>
      </Tabs>
    </div>
  );
}


/* ============================================================
   LEADS TAB
============================================================ */

const LEAD_STATUS_META = {
  new: { label: 'New', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  contacted: { label: 'Contacted', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  qualified: { label: 'Qualified', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  converted: { label: 'Converted', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  lost: { label: 'Lost', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function LeadsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  // FIX 2 (Task 5) — URL is the source of truth for the status filter.
  const status = searchParams.get('status') || 'all';
  const setStatus = (v) => {
    const next = new URLSearchParams(searchParams);
    if (!v || v === 'all') next.delete('status'); else next.set('status', v);
    setSearchParams(next);
  };
  const [search, setSearch] = useState('');
  const [openCount, setOpenCount] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (search) params.search = search;
      const res = await axios.get(`${API}/api/website-leads`, { params, withCredentials: true });
      setLeads(res.data?.items || []);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // FIX 1 (Task 5) — single backend metric for "open" leads (same number on the Dashboard chip).
  const loadStats = useCallback(() => {
    axios.get(`${API}/api/website-leads/stats`, { withCredentials: true })
      .then((r) => setOpenCount(r.data?.open ?? null)).catch(() => {});
  }, []);
  useEffect(() => { loadStats(); }, [loadStats, leads]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/api/website-leads/${leadId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Status updated');
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead? Cannot undo.')) return;
    try {
      await axios.delete(`${API}/api/website-leads/${leadId}`, { withCredentials: true });
      toast.success('Lead deleted');
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-panel">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, destination…"
            className="pl-9"
          />
        </div>
        {openCount != null ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 whitespace-nowrap" data-testid="leads-open-count">
            {openCount} open
          </span>
        ) : null}
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="leads-status-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open (active)</SelectItem>
            {Object.entries(LEAD_STATUS_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : leads.length === 0 ? (
          <div className="py-16 text-center text-slate-500 text-sm">
            <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            No leads yet. They will appear here as visitors submit the enquiry form on your website.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Lead ID</th>
                  <th className="px-4 py-3">Name / Contact</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.leadId} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`lead-row-${l.leadId}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{l.leadId}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{l.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        <Mail className="w-3 h-3" /> {l.email}
                      </div>
                      {l.phone && <div className="text-xs text-slate-500 flex items-center gap-2"><Phone className="w-3 h-3" /> {l.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{l.destination || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-xs capitalize">{l.source || 'website'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={l.status} onValueChange={(v) => updateStatus(l.leadId, v)}>
                        <SelectTrigger data-testid={`lead-status-${l.leadId}`} className="w-36 h-8">
                          <Badge className={`${LEAD_STATUS_META[l.status]?.color || ''} border font-medium`}>
                            {LEAD_STATUS_META[l.status]?.label || l.status}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(LEAD_STATUS_META).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(l.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => deleteLead(l.leadId)}
                          data-testid={`delete-lead-${l.leadId}`}
                        ><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}


/* ============================================================
   TESTIMONIALS TAB
============================================================ */

const EMPTY_TESTIMONIAL = {
  name: '', designation: '', photoUrl: '', rating: 5, quote: '', destination: '',
  source: 'manual', sourceUrl: '', featured: false, published: true, order: 0,
};

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TESTIMONIAL);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/testimonials`, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_TESTIMONIAL); setShowDialog(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY_TESTIMONIAL, ...t }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.name || !form.quote) { toast.error('Name and quote are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, rating: parseInt(form.rating, 10) || 5, order: parseInt(form.order, 10) || 0 };
      if (editing?.id) {
        await axios.put(`${API}/api/testimonials/${editing.id}`, payload, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/api/testimonials`, payload, { withCredentials: true });
        toast.success('Created');
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/api/testimonials/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="testimonials-panel">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="new-testimonial-btn">
          <Plus className="w-4 h-4 mr-2" /> New Testimonial
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card className="py-16 text-center text-slate-500 text-sm">
          <Star className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          No testimonials yet. Click "New Testimonial" to add your first one.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((t) => (
            <Card key={t.id} className="p-5" data-testid={`testimonial-card-${t.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  {t.photoUrl
                    ? <img src={t.photoUrl} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    : <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500">{t.name?.[0]}</div>}
                  <div>
                    <div className="font-semibold text-slate-800">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.designation || t.destination || ''}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= (t.rating || 5) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />)}</div>
                  {t.featured && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">FEATURED</Badge>}
                  {!t.published && <Badge className="bg-slate-200 text-slate-600 text-[10px]">DRAFT</Badge>}
                </div>
              </div>
              <p className="text-sm text-slate-600 italic mb-4">"{t.quote}"</p>
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-testimonial-${t.id}`}><Edit2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} data-testid={`delete-testimonial-${t.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} Testimonial</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input data-testid="testimonial-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Entrepreneur" /></div>
            </div>
            <div><Label>Photo URL</Label><Input value={form.photoUrl} onChange={(e) => setForm({ ...form, photoUrl: e.target.value })} placeholder="https://..." /></div>
            <div>
              <Label>Rating</Label>
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: parseInt(v, 10) })}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[5,4,3,2,1].map(n => <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Quote *</Label><textarea data-testid="testimonial-quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="w-full rounded-lg border border-slate-200 p-3 text-sm min-h-[120px]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Destination</Label><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Bali" /></div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="google">Google</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="tripadvisor">Tripadvisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="save-testimonial-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving</> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ============================================================
   SEO PAGES TAB
============================================================ */

const EMPTY_SEO = { slug: '', title: '', description: '', keywords: [], ogImage: '', canonicalUrl: '', published: true };

function SEOTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SEO);
  const [saving, setSaving] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/seo-pages`, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load SEO pages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_SEO); setKeywordInput(''); setShowDialog(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY_SEO, ...p }); setKeywordInput(''); setShowDialog(true); };

  const addKeyword = () => {
    const k = keywordInput.trim();
    if (!k) return;
    setForm({ ...form, keywords: [...(form.keywords || []), k] });
    setKeywordInput('');
  };

  const removeKeyword = (idx) => {
    setForm({ ...form, keywords: form.keywords.filter((_, i) => i !== idx) });
  };

  const handleSave = async () => {
    if (!form.slug || !form.title) { toast.error('Slug and title are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, keywords: form.keywords || [] };
      if (editing?.id) {
        await axios.put(`${API}/api/seo-pages/${editing.id}`, payload, { withCredentials: true });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/api/seo-pages`, payload, { withCredentials: true });
        toast.success('Created');
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SEO page?')) return;
    try {
      await axios.delete(`${API}/api/seo-pages/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="seo-panel">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="new-seo-btn">
          <Plus className="w-4 h-4 mr-2" /> New SEO Page
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card className="py-16 text-center text-slate-500 text-sm">
          <Globe className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          No SEO pages yet. Add one for each high-value route (home, about, destinations/bali, etc).
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <Card key={p.id} className="p-4 flex items-center justify-between" data-testid={`seo-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 rounded">/{p.slug}</span>
                  {p.published ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">PUBLISHED</Badge> : <Badge className="bg-slate-200 text-slate-600 text-[10px]">DRAFT</Badge>}
                </div>
                <div className="font-semibold text-slate-800 mt-1.5">{p.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{p.description}</div>
                {p.keywords?.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {p.keywords.slice(0, 5).map((k, i) => <span key={`${k}-${i}`} className="text-[10px] bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded">{k}</span>)}
                  </div>
                )}
              </div>
              <div className="flex gap-1 ml-4">
                <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-seo-${p.id}`}><Edit2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-seo-${p.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="seo-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} SEO Page</DialogTitle>
            <DialogDescription>Configure meta tags for a specific website route.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Slug * (e.g. "home", "destinations/bali")</Label>
              <Input data-testid="seo-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="home" />
            </div>
            <div>
              <Label>Title *</Label>
              <Input data-testid="seo-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Travcations — Luxury Travel Experiences" />
            </div>
            <div>
              <Label>Meta Description</Label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border border-slate-200 p-3 text-sm min-h-[80px]" />
            </div>
            <div>
              <Label>Keywords</Label>
              <div className="flex gap-2">
                <Input value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }} placeholder="Type + Enter" />
                <Button type="button" onClick={addKeyword} variant="outline">Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(form.keywords || []).map((k, i) => (
                  <span key={`kw-${k}-${i}`} className="text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded flex items-center gap-1">
                    {k}
                    <button onClick={() => removeKeyword(i)} className="text-teal-900 hover:text-rose-500">×</button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <Label>Open Graph Image URL</Label>
              <Input value={form.ogImage} onChange={(e) => setForm({ ...form, ogImage: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label>Canonical URL</Label>
              <Input value={form.canonicalUrl} onChange={(e) => setForm({ ...form, canonicalUrl: e.target.value })} placeholder="https://travcations.com/..." />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published (returned by /api/public/seo/{'{slug}'})
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="save-seo-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving</> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
